import { computed, nextTick, onBeforeUnmount, ref, watch, createVNode } from 'vue'
import { Modal, message as Message } from 'ant-design-vue'
import moment from 'moment'
import {
  getWeComGroupChatHistory,
  getWeComGroupTakeoverStatus,
  offlineWeComGroupTakeover,
  onlineWeComGroupTakeover,
  sendWeComGroupMedia,
  uploadWeComMediaFile,
  sendWeComGroupText
} from '@/api/workPlace/enterpriseWeChatRecord'
import { ExclamationCircleOutlined } from '@ant-design/icons-vue'
const POLL_INTERVAL = 5000
const MAX_MESSAGE_LENGTH = 4000

// 上传接口返回的文件名可能不含扩展名，发送企微图片前使用原文件名补齐。
const appendFileExtension = (name, fileName) => {
  const targetName = String(name || '')
  const originalName = String(fileName || '')
  const lastDot = originalName.lastIndexOf('.')
  if (!targetName) return originalName
  if (lastDot < 0 || /\.[^./]+$/.test(targetName)) return targetName
  return `${targetName}${originalName.slice(lastDot)}`
}

/**
 * 微信患者私聊模块的数据与交互逻辑。
 * 独立维护消息、分页、轮询及发送状态，避免与原有 IM 会话逻辑耦合。
 */
export default function useWechatPatientChat(props, emit) {
  // 当前会话已加载的企微消息列表。
  const messages = ref([])
  // 首次查询聊天记录的加载状态。
  const loading = ref(false)
  // 向上分页加载历史消息的加载状态。
  const loadingMore = ref(false)
  // 是否存在可展示的企微会话：当前在群或已退群但保留历史会话。
  const joined = ref(false)
  // 当前患者的微信模块是否已完成首次状态初始化。
  const initialized = ref(false)
  // 患者当前是否处于退群状态。
  const exited = ref(false)
  // 展示在功能栏上方的退群说明。
  const exitDetail = ref('')
  // 是否还有更早的聊天记录可加载。
  const hasMore = ref(false)
  // 下一次向上分页查询使用的消息序号。
  const nextBeforeSeq = ref(null)
  // 单次向上加载的消息条数。
  const pageSize = ref(20)
  // 首次查询聊天记录失败时的页面提示。
  const errorText = ref('')
  // 消息滚动容器的元素引用。
  const scrollRef = ref(null)
  // 当前滚动位置是否位于消息列表顶部。
  const atTop = ref(true)
  // 当前滚动位置是否位于消息列表底部。
  const atBottom = ref(true)
  // 用户未停留在底部时累计的新消息数量。
  const newMsgCount = ref(0)
  // 当前工单是否已进入人工接管模式。
  const isManualIntervention = ref(false)
  // 查询或切换企微群接管状态时的加载状态。
  const takeoverLoading = ref(false)
  // 文本消息被轮询确认后递增，通知输入区关闭发送中弹窗。
  const sendCompletedId = ref(0)
  // 发送接口失败后递增，通知输入区展示失败态。
  const sendFailedId = ref(0)
  let pendingTextMessage = null
  let pollTimer = null
  let takeoverRequestSerial = 0
  // 首次加载和轮询共用该锁，防止相同会话产生并发请求。
  let requesting = false

  // 后端以医生和患者共同确定唯一的企微私聊会话。
  const params = computed(() => ({
    bizType: 'DOCTOR_PATIENT',
    ucDoctorId: String(props.doctorId || ''),
    ucPatientId: String(props.patientId || '')
  }))

  const requestHistory = payload => getWeComGroupChatHistory({ ...params.value, ...payload })

  /**
   * 按 msgId 去重后按 seq 排序。
   * 轮询新增消息时，用户停留在底部则自动跟随，否则累计新消息提示。
   */
  const mergeMessages = (records = [], prepend = false) => {
    const oldIds = new Set(messages.value.map(item => item.msgId))
    const incomingCount = records.filter(item => !oldIds.has(item.msgId)).length
    const map = new Map(messages.value.map(item => [item.msgId, item]))
    records.forEach(item => map.set(item.msgId, item))
    const merged = Array.from(map.values()).sort((a, b) => a.seq - b.seq)
    messages.value = merged
    if (!prepend && pendingTextMessage) {
      const matched = records.some(item => {
        const text = item?.content?.text ?? item?.sendContent ?? ''
        return (
          Number(item?.memberType) === 0 &&
          Number(item?.seq) > pendingTextMessage.afterSeq &&
          String(text) === pendingTextMessage.content
        )
      })
      if (matched) {
        pendingTextMessage = null
        sendCompletedId.value += 1
      }
    }
    if (!prepend && atBottom.value) nextTick(scrollBottom)
    else if (!prepend) newMsgCount.value += incomingCount
  }

  const formatExitTime = time => {
    if (!time) return ''
    const value = moment(time)
    if (!value.isValid()) return String(time)
    return value.format('YYYY-M-D HH:mm')
  }

  const getExitDetail = () => {
    if (props.eligibility?.quitDetailLoaded !== true) return '患者已退群'
    const quitTime = formatExitTime(props.eligibility?.quitGroupTime)
    const quitText = quitTime ? `患者已退群（${quitTime}）` : '患者已退群'
    if (props.eligibility?.deleteAssistantFlag !== true) return `${quitText}，未删智能医助`
    const deleteTime = formatExitTime(props.eligibility?.deleteAssistantTime)
    return deleteTime ? `${quitText}，删智能医助 （${deleteTime}）` : `${quitText}，删智能医助`
  }

  // 加群和退群状态只消费白名单相关接口结果，不依赖聊天记录接口字段。
  const applyEligibility = () => {
    const inGroup = props.eligibility?.inGroup === true
    const quitGroupFlag = props.eligibility?.quitGroupFlag === true
    const hasExited = !inGroup && quitGroupFlag
    joined.value = inGroup || hasExited
    exited.value = hasExited
    exitDetail.value = hasExited ? getExitDetail() : ''
  }

  // 首次进入或切换患者时重置消息列表，并获取最新一页数据。
  const queryInitial = async () => {
    if (!params.value.ucDoctorId || !params.value.ucPatientId || requesting) return
    applyEligibility()
    initialized.value = true
    if (!joined.value) {
      messages.value = []
      hasMore.value = false
      nextBeforeSeq.value = null
      emit('joined-change', false)
      return
    }
    requesting = true
    loading.value = true
    errorText.value = ''
    try {
      const result = await requestHistory({ limit: Number(pageSize.value) || 20 })
      messages.value = []
      mergeMessages(result?.records)
      hasMore.value = Boolean(result?.hasMore)
      nextBeforeSeq.value = result?.nextBeforeSeq ?? null
      initialized.value = true
      emit('joined-change', joined.value)
    } catch (error) {
      errorText.value = '企微消息加载失败，请稍后重试'
    } finally {
      requesting = false
      loading.value = false
      // loading 期间消息容器未挂载，需等待容器重新渲染后再立即定位到底部。
      if (!errorText.value && messages.value.length) {
        await nextTick()
        const el = scrollRef.value
        if (el) {
          el.scrollTop = el.scrollHeight
          atBottom.value = true
          newMsgCount.value = 0
        }
      }
    }
  }

  // 向上翻页后补偿滚动高度，使用户仍停留在加载前看到的消息位置。
  const loadMore = async () => {
    const limit = Number(pageSize.value)
    if (!Number.isInteger(limit) || limit < 1 || limit > 100 || !hasMore.value || loadingMore.value)
      return
    loadingMore.value = true
    const el = scrollRef.value
    const oldHeight = el?.scrollHeight || 0
    try {
      const result = await requestHistory({ beforeSeq: nextBeforeSeq.value, limit })
      mergeMessages(result?.records, true)
      hasMore.value = Boolean(result?.hasMore)
      nextBeforeSeq.value = result?.nextBeforeSeq ?? null
      await nextTick()
      if (el) el.scrollTop = el.scrollHeight - oldHeight
    } finally {
      loadingMore.value = false
    }
  }

  // 轮询只在当前 Tab 激活且已建立企微会话时执行。
  const poll = async () => {
    if (!props.active || !joined.value || requesting) return
    requesting = true
    try {
      const result = await requestHistory({ limit: Number(pageSize.value) || 20 })
      mergeMessages(result?.records)
    } finally {
      requesting = false
    }
  }

  const startPolling = () => {
    stopPolling()
    if (props.polling === false) return
    pollTimer = window.setInterval(poll, POLL_INTERVAL)
  }
  const stopPolling = () => {
    if (pollTimer) window.clearInterval(pollTimer)
    pollTimer = null
  }

  const resetManualIntervention = () => {
    takeoverRequestSerial += 1
    pendingTextMessage = null
    isManualIntervention.value = false
    takeoverLoading.value = false
  }

  // 患者当前在群时，微信模块初始化恢复当前工单的人工接管状态。
  const queryTakeoverStatus = async () => {
    if (props.readonly) return
    const ucDoctorId = String(props.doctorId || '')
    const ucPatientId = String(props.patientId || '')
    const workOrderId = props.workOrderId
    if (props.eligibility?.inGroup !== true || !ucDoctorId || !ucPatientId || !workOrderId) return

    const currentSerial = ++takeoverRequestSerial
    takeoverLoading.value = true
    try {
      const result = await getWeComGroupTakeoverStatus({
        bizType: 'DOCTOR_PATIENT',
        ucDoctorId,
        ucPatientId,
        workOrderId
      })
      if (currentSerial !== takeoverRequestSerial) return
      isManualIntervention.value = result?.takenOver === true
    } catch {
      if (currentSerial !== takeoverRequestSerial) return
      isManualIntervention.value = false
    } finally {
      if (currentSerial === takeoverRequestSerial) takeoverLoading.value = false
    }
  }

  // 确认后调用上线或下线接口，成功后才切换人工状态。
  const changeManualIntervention = async ({
    endingManualIntervention,
    ucDoctorId,
    ucPatientId,
    workOrderId
  }) => {
    const currentSerial = ++takeoverRequestSerial
    takeoverLoading.value = true
    try {
      const request = endingManualIntervention
        ? offlineWeComGroupTakeover
        : onlineWeComGroupTakeover
      await request({
        bizType: 'DOCTOR_PATIENT',
        ucDoctorId,
        ucPatientId,
        workOrderId
      })
      if (currentSerial !== takeoverRequestSerial) return
      isManualIntervention.value = !endingManualIntervention
      Message.success('操作成功')
    } finally {
      if (currentSerial === takeoverRequestSerial) takeoverLoading.value = false
    }
  }

  const toggleManualIntervention = () => {
    if (takeoverLoading.value || exited.value || !joined.value) return

    const ucDoctorId = String(props.doctorId || '')
    const ucPatientId = String(props.patientId || '')
    const workOrderId = props.workOrderId
    if (!ucDoctorId || !ucPatientId || !workOrderId) {
      Message.warning('缺少企微人工接管参数')
      return
    }

    const endingManualIntervention = isManualIntervention.value
    Modal.confirm({
      title: endingManualIntervention
        ? '确定结束人工服务，AI接管继续服务？'
        : '确定暂停AI服务，人工介入？',
      icon: createVNode(ExclamationCircleOutlined),
      centered: true,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await changeManualIntervention({
            endingManualIntervention,
            ucDoctorId,
            ucPatientId,
            workOrderId
          })
        } catch {
          // 接口错误由请求层统一提示；吞掉异常以确保确认弹窗关闭。
        }
      }
    })
  }

  const sendText = async text => {
    const content = String(text || '').trim()
    if (
      !content ||
      content.length > MAX_MESSAGE_LENGTH ||
      !isManualIntervention.value ||
      exited.value
    )
      return

    const ucDoctorId = String(props.doctorId || '')
    const ucPatientId = String(props.patientId || '')
    const workOrderId = props.workOrderId
    if (!ucDoctorId || !ucPatientId || !workOrderId) {
      Message.warning('缺少企微消息发送参数')
      return
    }

    pendingTextMessage = {
      content,
      afterSeq: Math.max(0, ...messages.value.map(item => Number(item.seq) || 0))
    }
    try {
      await sendWeComGroupText({
        bizType: 'DOCTOR_PATIENT',
        ucDoctorId,
        ucPatientId,
        workOrderId,
        content
      })
    } catch {
      pendingTextMessage = null
      sendFailedId.value += 1
      Message.error('消息发送失败，请稍后重试')
    }
  }

  const sendImage = async ({ file }) => {
    if (!file || !isManualIntervention.value || exited.value) return

    const ucDoctorId = String(props.doctorId || '')
    const ucPatientId = String(props.patientId || '')
    const workOrderId = props.workOrderId
    if (!ucDoctorId || !ucPatientId || !workOrderId) {
      Message.warning('缺少企微图片发送参数')
      return
    }

    try {
      const uploadedFile = await uploadWeComMediaFile(file)
      const fileUrl = uploadedFile.path
      const fileName = appendFileExtension(uploadedFile.name || file.name, file.name)
      await sendWeComGroupMedia({
        bizType: 'DOCTOR_PATIENT',
        ucDoctorId,
        ucPatientId,
        workOrderId,
        fileUrl,
        fileName
      })
    } catch {
      sendFailedId.value += 1
      Message.error('图片发送失败，请稍后重试')
    }
  }

  // 跟踪滚动位置，用于控制回到顶部、回到底部及新消息提示。
  const onScroll = () => {
    const el = scrollRef.value
    if (!el) return
    atTop.value = el.scrollTop === 0
    atBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
    if (atBottom.value) newMsgCount.value = 0
  }
  const scrollTop = () => scrollRef.value?.scrollTo({ top: 0, behavior: 'smooth' })
  const scrollBottom = () =>
    scrollRef.value?.scrollTo({ top: scrollRef.value.scrollHeight, behavior: 'smooth' })
  const handleNewMsgTips = () => {
    newMsgCount.value = 0
    scrollBottom()
  }

  watch(
    () => [
      props.doctorId,
      props.patientId,
      props.workOrderId,
      props.eligibility?.inGroup,
      props.eligibility?.quitGroupFlag,
      props.eligibility?.quitDetailLoaded,
      props.eligibility?.quitGroupTime,
      props.eligibility?.deleteAssistantFlag,
      props.eligibility?.deleteAssistantTime
    ],
    () => {
      applyEligibility()
      resetManualIntervention()
      queryTakeoverStatus()
    },
    { immediate: true }
  )

  // Tab 或会话身份变化时重建轮询，避免旧患者的定时任务继续请求。
  watch(
    () => [
      props.active,
      props.doctorId,
      props.patientId,
      props.eligibility?.inGroup,
      props.eligibility?.quitGroupFlag,
      props.eligibility?.quitDetailLoaded,
      props.eligibility?.quitGroupTime,
      props.eligibility?.deleteAssistantFlag,
      props.eligibility?.deleteAssistantTime
    ],
    ([active], oldValue = []) => {
      stopPolling()
      if (!active) return
      applyEligibility()
      const identityChanged =
        oldValue[1] !== props.doctorId ||
        oldValue[2] !== props.patientId ||
        oldValue[3] !== props.eligibility?.inGroup ||
        oldValue[4] !== props.eligibility?.quitGroupFlag
      if (!joined.value) {
        messages.value = []
        initialized.value = true
        return
      }
      if (!initialized.value || identityChanged)
        queryInitial().then(() => joined.value && props.polling !== false && startPolling())
      else if (joined.value) {
        if (props.polling !== false) {
          poll()
          startPolling()
        }
      }
    },
    { immediate: true }
  )

  onBeforeUnmount(stopPolling)

  return {
    messages,
    loading,
    loadingMore,
    joined,
    initialized,
    exited,
    exitDetail,
    hasMore,
    pageSize,
    errorText,
    scrollRef,
    atTop,
    atBottom,
    newMsgCount,
    isManualIntervention,
    takeoverLoading,
    sendCompletedId,
    sendFailedId,
    toggleManualIntervention,
    queryInitial,
    loadMore,
    sendText,
    sendImage,
    onScroll,
    scrollTop,
    scrollBottom,
    handleNewMsgTips
  }
}
